import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import AuthLogin from '../profile/auth-login';
import '../assets/styles/MyAccount.css';
import LoggedInHeader from '../components/LoggedInHeader';
import PaymentMethods from '../components/PaymentMethods';
import SubscriptionUpgrade from '../components/SubscriptionUpgrade';
import AddCard from '../components/AddCard';
import { STRIPE_PUBLISHABLE_KEY } from '../config';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// Custom Modal Component
const Modal = ({ isOpen, onClose, title, children, actions }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {children}
        </div>
        {actions && (
          <div className="modal-footer">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

const MyAccount = () => {
  const ctx = useContext(AuthLogin);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [accountDetails, setAccountDetails] = useState({
    user: null,
    subscription: null,
    billing: null
  });
  
  // Form states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  // Renewal modal states
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddCard, setShowAddCard] = useState(false);
  const [renewalError, setRenewalError] = useState('');
  const [renewalLoading, setRenewalLoading] = useState(false);

  const loadAccountDetails = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/account-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          userId: ctx.userId,
          password: ctx.encryptedPassword
        })
      });

      if (!response.ok) {
        throw new Error('Failed to load account details');
      }

      const data = await response.json();
      setAccountDetails(data);
    } catch (error) {
      console.error('Error loading account details:', error);
      setLoadError('Failed to load account details. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [ctx.userId, ctx.encryptedPassword]);

  useEffect(() => {
    loadAccountDetails();
  }, [loadAccountDetails]);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/payment-methods/${ctx.userId}/${ctx.encryptedPassword}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch payment methods');
      }

      const data = await response.json();
      setPaymentMethods(data);
      
      // Auto-select the default payment method if available
      const defaultMethod = data.find(method => method.isDefault);
      if (defaultMethod) {
        setSelectedPaymentMethod(defaultMethod.id);
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
      setRenewalError('Failed to load payment methods');
    }
  }, [ctx.userId, ctx.encryptedPassword]);


  const validatePassword = (password) => {
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (!currentPassword) {
      setFormError('Current password is required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setFormError('New passwords do not match');
      return;
    }

    const passwordChecks = validatePassword(newPassword);
    if (!Object.values(passwordChecks).every(Boolean)) {
      setFormError('Password must be at least 8 characters and include uppercase, lowercase, numbers, and special characters');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user: ctx.user,
          userId: ctx.userId,
          currentPassword: currentPassword,
          newPassword: newPassword
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to change password');
      }

      setFormSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordChange(false);
    } catch (error) {
      setFormError(error.message);
    }
  };

  const handleOpenRenewalModal = () => {
    setRenewalError('');
    setRenewalLoading(false);
    setSelectedPlan('monthly');
    setSelectedPaymentMethod(null);
    setShowRenewalModal(true);
    fetchPaymentMethods();
  };

  /**
   * Handle subscription renewal submission
   * 
   * Handles TWO types of renewals:
   * 
   * IMMEDIATE RENEWAL (no active cancelled subscription):
   * 1. /renew-subscription - Creates Stripe subscription (status: "incomplete")
   * 2. stripe.confirmCardPayment() - Charges the customer's card immediately
   * 3. /confirm-renewal - Creates database record
   * 
   * SCHEDULED RENEWAL (has cancelled-but-active subscription):
   * 1. /renew-subscription - Creates Stripe subscription with trial_end (status: "trialing")
   * 2. No payment confirmation needed (billing deferred until current sub expires)
   * 3. /confirm-renewal - Creates database record with future start_date
   */
  const handleRenewalSubmit = async () => {
    setRenewalError('');
    setRenewalLoading(true);
    
    if (!selectedPlan || !selectedPaymentMethod) {
      setRenewalError('Please select both a plan and payment method');
      setRenewalLoading(false);
      return;
    }

    try {
      // ============================================================
      // STEP 1: Create Stripe subscription
      // ============================================================
      const response = await fetch(`${process.env.REACT_APP_API_URL}/renew-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: ctx.userId,
          password: ctx.encryptedPassword,
          subscriptionType: selectedPlan,
          paymentMethodId: selectedPaymentMethod
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create subscription');
      }

      const data = await response.json();
      
      // Check if this is a scheduled renewal (has scheduledStartDate)
      const isScheduledRenewal = !!data.scheduledStartDate;

      // ============================================================
      // STEP 2: Confirm payment (ONLY for immediate renewals)
      // ============================================================
      // Scheduled renewals use trial_end, so no immediate payment needed
      if (data.requiresPaymentConfirmation && data.clientSecret) {
        const stripe = await stripePromise;
        
        if (!stripe) {
          throw new Error('Stripe failed to initialize. Please refresh and try again.');
        }

        const { error: confirmError } = await stripe.confirmCardPayment(data.clientSecret);
        
        if (confirmError) {
          throw new Error(confirmError.message || 'Payment confirmation failed');
        }
      }

      // ============================================================
      // STEP 3: Confirm renewal with backend (create database record)
      // ============================================================
      // Pass scheduledStartDate for scheduled renewals
      const confirmResponse = await fetch(`${process.env.REACT_APP_API_URL}/confirm-renewal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: ctx.userId,
          password: ctx.encryptedPassword,
          stripeSubscriptionId: data.subscription.id,
          subscriptionType: selectedPlan,
          paymentMethodId: selectedPaymentMethod,
          scheduledStartDate: data.scheduledStartDate || null  // For scheduled renewals
        })
      });

      if (!confirmResponse.ok) {
        const errorData = await confirmResponse.json();
        console.error('Database update failed:', errorData);
        throw new Error(errorData.error || 'Failed to update account. Please contact support.');
      }

      // ============================================================
      // STEP 4: Update frontend state and show success
      // ============================================================
      if (isScheduledRenewal) {
        // Scheduled: User keeps current subscription, new one starts later
        // Don't update isSubscribed - they're still using the old subscription
        const startDate = new Date(data.scheduledStartDate).toLocaleDateString();
        setFormSuccess(`Your ${selectedPlan} subscription is scheduled to start on ${startDate}`);
      } else {
        // Immediate: Update context so user gets immediate access
        ctx.updateSubscriptionStatus(true);
        setFormSuccess('Subscription renewed successfully!');
      }

      await loadAccountDetails();
      setShowRenewalModal(false);
      setTimeout(() => setFormSuccess(''), 5000);

    } catch (error) {
      console.error('Renewal error:', error);
      setRenewalError(error.message || 'Failed to renew subscription. Please try again.');
    } finally {
      setRenewalLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Check if a subscription is currently active (providing access right now)
  // Must have: started (start_date <= now), not ended (end_date > now), not cancelled
  const isSubscriptionActive = (sub) => {
    if (!sub) return false;
    if (sub.cancelled) return false;
    if (!sub.end_date) return false;
    
    const now = Date.now();
    const start = sub.start_date ? new Date(sub.start_date).getTime() : 0;
    const end = new Date(sub.end_date).getTime();
    
    // Subscription is active only if it has started AND hasn't ended
    return start <= now && end > now;
  };

  const handleCancelMembership = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: ctx.userId,
        })
      });

      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }

      await loadAccountDetails();
      setFormSuccess('Subscription cancelled successfully');
      setShowCancelConfirm(false);
      setTimeout(() => setFormSuccess(''), 3000);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanChange = async (newPriceId) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/update-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: ctx.userId,
          newPriceId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update subscription');
      }

      await loadAccountDetails();
      setFormSuccess('Subscription updated successfully');
      setShowUpgradeConfirm(false);
      setTimeout(() => setFormSuccess(''), 3000);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setIsLoading(false);
    }
  };



  if (isLoading) {
    return (
      <>
        <LoggedInHeader />
        <div className="my-account-container">
          <div className="loading-message">Loading account details...</div>
        </div>
      </>
    );
  }

  if (loadError) {
    return (
      <>
        <LoggedInHeader />
        <div className="my-account-container">
          <div className="error-section">
            <div className="error-message">{loadError}</div>
            <button 
              className="action-button"
              onClick={loadAccountDetails}
            >
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  // Extract subscription and scheduledRenewal from account details
  const { user, subscription, scheduledRenewal } = accountDetails;

  return (
    <>
      <LoggedInHeader />
      <div className="my-account-container">
        <h1>My Account</h1>
        
        {formError && <div className="error-message">{formError}</div>}
        {formSuccess && <div className="success-message">{formSuccess}</div>}
        
        <div className="account-section">
          <h2>Profile Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Username</label>
              <p>{user?.username || 'N/A'}</p>
            </div>
            <div className="info-item">
              <label>Name</label>
              <p>{user ? `${user.firstname} ${user.lastname}` : 'N/A'}</p>
            </div>
            <div className="info-item">
              <label>Email</label>
              <p>{user?.email || 'N/A'}</p>
            </div>
          </div>
        </div>

        <div className="account-section">
          <h2>Subscription Details</h2>
          {subscription ? (
            <div className="info-grid">
              <div className="info-item">
                <label>Plan Type</label>
                <p>{subscription.subscription_type || 'N/A'}</p>
              </div>
              <div className="info-item">
                <label>Status</label>
                <p>
                  <span className={`status-indicator ${subscription.cancelled ? 'cancelled' : 'active'}`}>
                    {subscription.cancelled ? 'Cancelled' : 'Active'}
                  </span>
                </p>
              </div>
              <div className="info-item">
                <label>Start Date</label>
                <p>{formatDate(subscription.start_date)}</p>
              </div>
              <div className="info-item">
                <label>{subscription.cancelled ? 'End Date' : 'Renewal Date'}</label>
                <p>{formatDate(subscription.end_date)}</p>
              </div>
              {isSubscriptionActive(subscription) && (
                <>
                  <div className="info-item full-width">
                    <button 
                      className="action-button change-plan"
                      onClick={() => setShowUpgradeConfirm(true)}
                    >
                      Change Plan
                    </button>
                  </div>
                  <div className="info-item full-width">
                    <button 
                      className="action-button cancel-membership"
                      onClick={() => setShowCancelConfirm(true)}
                    >
                      Cancel Membership
                    </button>
                  </div>
                </>
              )}
              {/* 
                Show the Renew button when:
                1. No scheduled renewal exists (prevent double scheduling), AND
                2. Either subscription is cancelled OR subscription has expired
              */}
              {!scheduledRenewal && (subscription.cancelled || !isSubscriptionActive(subscription)) && (
                <div className="info-item full-width">
                  <button 
                    className="action-button renew-subscription"
                    onClick={handleOpenRenewalModal}
                  >
                    Renew
                  </button>
                </div>
              )}
            </div>
          ) : (
            <p className="no-data-message">No subscription information available.</p>
          )}

          {/* Display scheduled renewal info if exists */}
          {scheduledRenewal && (
            <div className="scheduled-renewal-notice" style={{
              marginTop: '1rem',
              padding: '1rem',
              backgroundColor: '#e8f4fd',
              borderRadius: '8px',
              border: '1px solid #b8daff'
            }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#004085' }}>
                📅 Upcoming Subscription
              </h4>
              <p style={{ margin: 0, color: '#004085' }}>
                Your <strong>{scheduledRenewal.subscription_type}</strong> subscription 
                will begin on <strong>{formatDate(scheduledRenewal.start_date)}</strong>
              </p>
            </div>
          )}
        </div>

        <div className="account-section">
          <div className="section-header">
            <h2>Billing Information</h2>
          </div>
          
          {/* Payment Methods Component */}
          <PaymentMethods />
        </div>

        <div className="account-section">
          <h2>Account Security</h2>
          {!showPasswordChange ? (
            <button 
              className="action-button"
              onClick={() => setShowPasswordChange(true)}
            >
              Change Password
            </button>
          ) : (
            <form className="password-form">
              {formError && <div className="error-message">{formError}</div>}
              {formSuccess && <div className="success-message">{formSuccess}</div>}
              <div className="form-group">
                <input
                  type="password"
                  placeholder="Current Password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="password"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <div className="button-group">
                <button type="submit" 
                className="submit-button"
                onClick={handlePasswordChange}>
                  Update Password
                </button>
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setFormError('');
                    setFormSuccess('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Cancel Confirmation Modal */}
        <Modal
          isOpen={showCancelConfirm}
          onClose={() => setShowCancelConfirm(false)}
          title="Cancel Membership"
          actions={
            <>
              <button 
                className="modal-button secondary" 
                onClick={() => setShowCancelConfirm(false)}
              >
                Keep Membership
              </button>
              <button 
                className="modal-button primary danger" 
                onClick={handleCancelMembership}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : 'Yes, Cancel'}
              </button>
            </>
          }
        >
          <p>Are you sure you want to cancel your membership? Your subscription will remain active until {formatDate(subscription?.end_date)}.</p>
        </Modal>

        {/* Upgrade/Downgrade Modal */}
        <Modal
          isOpen={showUpgradeConfirm}
          onClose={() => setShowUpgradeConfirm(false)}
          title="Change Plan"
        >
          <SubscriptionUpgrade
            currentPlan={subscription?.subscription_type}
            onUpgrade={handlePlanChange}
            isLoading={isLoading}
          />
        </Modal>

        {/* Renewal Modal */}
        <Modal
          isOpen={showRenewalModal}
          onClose={() => setShowRenewalModal(false)}
          title="Renew Subscription"
          actions={
            <>
              <button 
                className="modal-button secondary" 
                onClick={() => setShowRenewalModal(false)}
              >
                Cancel
              </button>
              <button 
                className="modal-button primary" 
                onClick={handleRenewalSubmit}
                disabled={!selectedPlan || !selectedPaymentMethod || renewalLoading}
              >
                {renewalLoading ? 'Processing...' : 'Renew Subscription'}
              </button>
            </>
          }
        >
          <div className="renewal-modal-content">
            {renewalError && (
              <div className="error-message">{renewalError}</div>
            )}
            
            {/* Plan Selection */}
            <div className="renewal-section">
              <h4>Select Plan</h4>
              <div className="pricing-cards">
                <div 
                  className={`pricing-card ${selectedPlan === 'monthly' ? 'selected' : ''}`}
                  onClick={() => setSelectedPlan('monthly')}
                >
                  <input
                    type="radio"
                    name="plan"
                    value="monthly"
                    checked={selectedPlan === 'monthly'}
                    onChange={() => setSelectedPlan('monthly')}
                  />
                  <h2 className="plan-title">Monthly</h2>
                  <div className="price">$4.99</div>
                  <div className="interval">/month</div>
                                     <ul className="features">
                     <li>ALL FEATURES INCLUDED</li>
                   </ul>
                </div>
                
                <div 
                  className={`pricing-card ${selectedPlan === 'annual' ? 'selected' : ''}`}
                  onClick={() => setSelectedPlan('annual')}
                >
                  <input
                    type="radio"
                    name="plan"
                    value="annual"
                    checked={selectedPlan === 'annual'}
                    onChange={() => setSelectedPlan('annual')}
                  />
                  <h2 className="plan-title">Annual</h2>
                  <div className="price">$49.99</div>
                  <div className="interval">/year</div>
                                     <ul className="features">
                     <li>ALL FEATURES INCLUDED</li>
                     <li>2 MONTHS FREE</li>
                   </ul>
                </div>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="renewal-section">
              <div className="payment-header">
                <h4>Select Payment Method</h4>
                <button 
                  className="payment-add-button"
                  onClick={() => setShowAddCard(true)}
                >
                  Add New Card
                </button>
              </div>
              
              <div className="payment-list">
                {paymentMethods.map((method) => (
                  <div key={method.id} className="payment-card">
                    <div className="payment-card-content">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method.id}
                        checked={selectedPaymentMethod === method.id}
                        onChange={() => setSelectedPaymentMethod(method.id)}
                      />
                      <div className="payment-details">
                        <div className="payment-card-header">
                          <span className="card-brand">{method.brand}</span>
                          <span className="card-number">•••• {method.last4}</span>
                          {method.isDefault && (
                            <span className="default-badge">Default</span>
                          )}
                        </div>
                        <div className="expiry-date">
                          Expires {method.expMonth.toString().padStart(2, '0')}/{method.expYear.toString().slice(-2)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>

        {/* Add Card Modal */}
        {showAddCard && (
          <Elements stripe={stripePromise}>
            <AddCard 
              onClose={() => setShowAddCard(false)}
              onSuccess={() => {
                fetchPaymentMethods();
                setShowAddCard(false);
              }}
            />
          </Elements>
        )}
      </div>
    </>
  );
};

export default MyAccount;